import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { GqlTalentJobFunnel } from '@bloomays-lib/types.shared';
import { UPDATE_MARKED_AS_SEEN } from '@bloomays-lib/adapter.api-talent';
import { Logger } from '../services/service-logger';
import { monitorException } from '@bloomays-lib/common.monitoring/browser';

const logger = Logger('useUpdateMarkedAsSeen');

type UpdateMarkedAsSeenVariables = {
  talentId: string;
  talentName: string;
  jobId: string;
  jobName: string;
  markedAsSeen: boolean;
};

export const useUpdateMarkedAsSeen = () => {
  const [updateMarkedAsSeenMutation, { loading, error }] = useMutation<
    { updateMarkedAsSeen: GqlTalentJobFunnel | null },
    UpdateMarkedAsSeenVariables
  >(UPDATE_MARKED_AS_SEEN, {
    context: { clientName: 'api.talents' },
  });

  const executeUpdate = useCallback(
    async (
      talentId: string,
      talentName: string,
      jobId: string,
      jobName: string,
      markedAsSeen: boolean,
    ): Promise<boolean> => {
      try {
        const result = await updateMarkedAsSeenMutation({
          variables: {
            talentId,
            talentName,
            jobId,
            jobName,
            markedAsSeen,
          },
        });

        return result && result.data?.updateMarkedAsSeen ? true : false;
      } catch (err) {
        logger.error('Error updating marked as seen', { talentId, jobId, markedAsSeen }, err as Error);
        monitorException(err as Error, { name: 'useUpdateMarkedAsSeen', talentId, jobId, markedAsSeen });
        return false;
      }
    },
    [updateMarkedAsSeenMutation],
  );

  return {
    updateMarkedAsSeen: executeUpdate,
    loading,
    error,
  };
};

export default useUpdateMarkedAsSeen;
